import { Injectable } from '@angular/core';
import { LoggingService } from 'services/logging.service';

enum AnimationDebugLevel {
  OFF = 0,
  BASIC = 1,  // start/stop messages only
  FULL = 2    // all events
}

const DEBUG_ANIMATIONS = AnimationDebugLevel.OFF;

// an element carrying this attribute animates for as long as it shows live progress (the rest
// timer button's countdown fill), so waitAnimationsAsync does not wait for its animations
const CONTINUOUS_ANIMATION_ATTRIBUTE = 'data-continuous-animation';

@Injectable()
/**
Provides a way to wait for all CSS animations and transitions to complete.
Mostly used for E2E tests to make sure they don't attempt to click on things
while they are animated (and potentially miss the target).

Uses the Web Animations API (document.getAnimations()) for CSS animations/transitions,
plus manual tracking for JavaScript-driven animations via onAnimationStarted/onAnimationEnded.
*/
export class AnimationController {
  // Manual animation count for JS-driven animations that don't use Web Animations API
  private manualAnimationCount = 0;
  // DEBUG: track pending animation callers by stack trace (FIFO queue)
  private pendingCallers: string[] = [];

  constructor(private logging: LoggingService) {
    window['waitAnimationsAsync'] = async () => {
      return this.waitAnimationsAsync();
    };
  }

  /**
   * Safely await multiple web animation promises.
   * Handles cancellation gracefully (cancelled animations reject, we catch and ignore).
   */
  async safeAwaitAnimations(animations: (Animation | undefined)[]): Promise<void> {
    const valid = animations.filter((a): a is Animation => a !== undefined);
    if (valid.length === 0) return;
    await Promise.all(valid.map(a => a.finished.catch(() => {})));
  }

  /**
   * Wrap an async animation operation with automatic tracking.
   * The counter is incremented at start and ALWAYS decremented at end (via finally).
   * Use this instead of manual onAnimationStarted()/onAnimationEnded() for async code.
   */
  async withAnimationTracking<T>(fn: () => Promise<T>): Promise<T> {
    this.onAnimationStarted();
    try {
      return await fn();
    } finally {
      this.onAnimationEnded();
    }
  }

  /**
   * For async code, use `withAnimationTracking()` instead.
   *
   * WARNING: Only use this for synchronous code with a guarded setTimeout pattern:
   * ```
   * if (!this.isAnimating) {
   *   this.isAnimating = true;
   *   this.animationController.onAnimationStarted();
   * }
   * // ... sync work ...
   * setTimeout(() => {
   *   if (this.isAnimating) {  // Guard required!
   *     this.isAnimating = false;
   *     this.animationController.onAnimationEnded();
   *   }
   * }, 0);
   * ```
   *
   * NEVER use with async/await - exceptions will skip onAnimationEnded()!
   */
  onAnimationStarted() {
    this.manualAnimationCount++;
    if (DEBUG_ANIMATIONS >= AnimationDebugLevel.FULL) {
      const stack = new Error().stack?.split('\n').slice(1, 8).map(s => s.trim()).join(' < ') || '?';
      this.pendingCallers.push(stack);
      console.log('[AnimationController] Manual animation started, count:', this.manualAnimationCount, stack);
    }
  }

  /**
   * For async code, use `withAnimationTracking()` instead.
   * @see onAnimationStarted for usage pattern
   */
  onAnimationEnded() {
    this.manualAnimationCount--;
    if (DEBUG_ANIMATIONS >= AnimationDebugLevel.FULL) {
      // pop oldest caller (FIFO — start/end should pair)
      if (this.pendingCallers.length > 0) {
        this.pendingCallers.shift();
      }
    }
    if (this.manualAnimationCount < 0) {
      console.warn('[AnimationController] Manual animation count went negative, resetting to 0');
      this.manualAnimationCount = 0;
    }
    if (DEBUG_ANIMATIONS >= AnimationDebugLevel.FULL) {
      console.log('[AnimationController] Manual animation ended, count:', this.manualAnimationCount);
    }
  }

  private shouldSkipAnimation(a: Animation): boolean {
    try {
      // Skip finished animations (e.g., fill: 'forwards' keeps them in the list but they're done)
      if (a.playState === 'finished') {
        return true;
      }
      // Skip infinite animations (e.g., loading spinners) - they never finish by design
      const timing = (a.effect as KeyframeEffect)?.getTiming?.();
      if (timing?.iterations === Infinity) {
        return true;
      }
      // skip animations on an element that marks itself as continuously animating (the rest
      // timer button's countdown fill restarts a 1 s transition every second for the whole
      // rest, even while its card is folded and the button hidden). like a spinner it never
      // settles, so waiting for it only burns the 2 s cap per round
      const target = (a.effect as KeyframeEffect)?.target;
      if (target instanceof Element && target.hasAttribute(CONTINUOUS_ANIMATION_ATTRIBUTE)) {
        return true;
      }
      // Skip stuck transitions — if currentTime far exceeds the expected duration,
      // the transition is stuck (e.g., background-color on buttons that never complete)
      const duration = typeof timing?.duration === 'number' ? timing.duration : 0;
      if (duration > 0 && typeof a.currentTime === 'number' && a.currentTime > duration * 3) {
        return true;
      }
      return false;
    } catch {
      // an animation whose timing cannot be read is waited for like any other, and
      // waitAnimationsAsync never waits longer than 2 s for a round of them
      return false;
    }
  }

  async waitAnimationsAsync() {
    // Use the Web Animations API to wait for all running animations,
    // plus check manual animation count for JS-driven animations
    let lastCount = -1;
    const maxIterations = 20; // max 2 seconds (20 × 100ms)

    for (let i = 0; i < maxIterations; i++) {
      // Filter out infinite animations (like loading spinners) - they never finish by design
      const webAnimations = document.getAnimations().filter(a => !this.shouldSkipAnimation(a));
      const totalCount = webAnimations.length + this.manualAnimationCount;

      if (DEBUG_ANIMATIONS >= AnimationDebugLevel.FULL) {
        console.log(`[waitAnimationsAsync] iteration ${i}, web animations: ${webAnimations.length}, manual: ${this.manualAnimationCount}`);
      }

      // if no animations for 2 consecutive checks, we're done
      if (totalCount === 0 && lastCount === 0) {
        if (DEBUG_ANIMATIONS >= AnimationDebugLevel.BASIC) {
          console.log('[waitAnimationsAsync] No animations detected');
        }
        return;
      }
      lastCount = totalCount;

      if (webAnimations.length > 0) {
        // Wait for current web animations to finish (or 2000ms, whichever comes first)
        // The .catch handles animations that get cancelled
        await Promise.race([
          Promise.all(webAnimations.map(a => a.finished.catch(() => { /* animation cancelled */ }))),
          new Promise<void>(r => setTimeout(r, 2000))
        ]);
      } else {
        // No web animations right now, but wait a bit for manual animations or new ones to start
        await new Promise<void>(r => setTimeout(r, 100));
      }
    }

    // Always warn when we hit max iterations - this indicates animations are still running
    const remainingWebAnimations = document.getAnimations().filter(a => !this.shouldSkipAnimation(a));
    const remainingTotal = remainingWebAnimations.length + this.manualAnimationCount;
    if (remainingTotal > 0) {
      const animDetails = remainingWebAnimations.map(a => {
        const target = (a.effect as KeyframeEffect)?.target as HTMLElement;
        const name = (a as any).animationName || (a as any).transitionProperty || a.constructor.name;
        const tagName = target?.tagName?.toLowerCase() || '?';
        const id = target?.id ? `#${target.id}` : '';
        const classes = target?.className ? `.${String(target.className).split(' ').slice(0, 2).join('.')}` : '';
        return `${name} on <${tagName}${id}${classes}>`;
      });
      // log pendingCallers as a string so it appears in WebDriver SEVERE logs (objects show as "Object")
      if (this.pendingCallers.length > 0) {
        console.error(`[AnimationController] LEAKED CALLERS:\n${this.pendingCallers.map((s, i) => `  ${i}: ${s}`).join('\n')}`);
      }
      this.logging.trackSilentProblem(`waitAnimationsAsync timeout (manual: ${this.manualAnimationCount}, web: ${remainingWebAnimations.length})`, {
        total: remainingTotal,
        webCount: remainingWebAnimations.length,
        manualCount: this.manualAnimationCount,
        animations: animDetails,
        pendingCallers: this.pendingCallers.slice()
      });
    }
  }
}
